import { call, put, takeEvery } from 'redux-saga/effects'
import {
  FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions,
  AcceptCustomerInviteDocusignCompleteSigning,
  AcceptCustomerInviteDocusignDeclineSigning,
  AcceptCustomerInviteDocusignStartSigning,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callSecureApi } from '../api/callApi'

interface AcceptCustomerInviteDocusignStartSigningRequest {
  signingFields?: Array<{
    tabLabel: string
    value: string
  }>
  embedded: boolean
  customerId: string
}

function* onAcceptCustomerInviteDocusignStartSigning(
  action: AsyncAction<
    AcceptCustomerInviteDocusignStartSigningRequest,
    DocusignStartSigningResponse
  >,
) {
  try {
    const { signingFields, embedded, customerId } = action.payload || {}

    const body: Omit<AcceptCustomerInviteDocusignStartSigningRequest, 'embedded'> = { customerId }

    if (signingFields?.length) {
      body.signingFields = signingFields
    }

    const { payload }: { payload: DocusignStartSigningResponse } = yield call(
      callSecureApi,
      Endpoints.AcceptCustomerInviteDocusignStartSigning,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body,
        query: {
          embedded: embedded.toString(),
        },
      },
    )

    yield put(AcceptCustomerInviteDocusignStartSigning.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch docusign signing url'

    yield put(AcceptCustomerInviteDocusignStartSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AcceptCustomerInviteDocusignCompleteSigningRequest {
  customerId: string
}

function* onAcceptCustomerInviteDocusignCompleteSigning(
  action: AsyncAction<AcceptCustomerInviteDocusignCompleteSigningRequest>,
) {
  try {
    const { customerId } = action.payload

    yield call(callSecureApi, Endpoints.AcceptCustomerInviteDocusignCompleteSigning, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        customerId,
      },
    })

    yield put(AcceptCustomerInviteDocusignCompleteSigning.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to complete signing'

    yield put(AcceptCustomerInviteDocusignCompleteSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AcceptCustomerInviteDocusignDeclineSigningRequest {
  customerId: string
}

function* onAcceptCustomerInviteDocusignDeclineSigning(
  action: AsyncAction<AcceptCustomerInviteDocusignDeclineSigningRequest>,
) {
  const { customerId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AcceptCustomerInviteDocusignDeclineSigning, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        customerId,
      },
    })

    yield put(AcceptCustomerInviteDocusignDeclineSigning.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to decline signing'

    yield put(AcceptCustomerInviteDocusignDeclineSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchSigningEnvelopeOptionsRequest {
  customerId: string
}

function* onFetchSigningEnvelopeOptions(
  action: AsyncAction<FetchSigningEnvelopeOptionsRequest, DocusignSigningEnvelopeOptionsResponse>,
) {
  try {
    const { customerId } = action.payload

    const { payload }: { payload: DocusignSigningEnvelopeOptionsResponse } = yield call(
      callSecureApi,
      Endpoints.FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query: {
          customerId,
        },
      },
    )

    yield put(FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage =
      err?.payload?.message || 'Failed to fetch docusign signing envelope options'

    yield put(FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* mcDocusignSaga() {
  yield takeEvery(
    AcceptCustomerInviteDocusignStartSigning.type.REQUEST,
    onAcceptCustomerInviteDocusignStartSigning,
  )
  yield takeEvery(
    AcceptCustomerInviteDocusignCompleteSigning.type.REQUEST,
    onAcceptCustomerInviteDocusignCompleteSigning,
  )
  yield takeEvery(
    AcceptCustomerInviteDocusignDeclineSigning.type.REQUEST,
    onAcceptCustomerInviteDocusignDeclineSigning,
  )
  yield takeEvery(
    FetchAcceptCustomerInviteDocusignSigningEnvelopeOptions.type.REQUEST,
    onFetchSigningEnvelopeOptions,
  )
}
